import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Package, AlertTriangle,
         ToggleLeft, ToggleRight, ChevronDown, ChevronUp, Image,
         BarChart2, Tag, Layers } from 'lucide-react';

const C = {
  bg:     '#070D0A', card:  '#0E1812', border: '#1C2E22',
  gold:   '#C9A84C', goldLt:'#E8C97A', text:  '#EDE8DC',
  muted:  'rgba(237,232,220,0.5)', red: '#ef4444',
  green:  '#22c55e', amber: '#f59e0b', input: '#111A14',
};

const CATEGORIES = ['Apparel','Memorabilia','Digital','Food','Tickets','Crafts','Books','Other'];
const VARIANT_TYPES = ['size','colour','type','material','gender'];
const SIZE_VALUES   = ['XS','S','M','L','XL','XXL','3XL','4XL','One Size'];
const COLOUR_VALUES = ['Black','White','Red','Blue','Green','Gold','Navy','Royal Blue','Custom'];
const GENDER_VALUES = ['Male','Female','Unisex','Boys','Girls'];

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('erue_auth_token') || ''}`, 'Content-Type': 'application/json' });

const iS: React.CSSProperties = {
  width:'100%', padding:'8px 10px', borderRadius:6,
  background:C.input, border:`1px solid ${C.border}`,
  color:C.text, fontSize:13, outline:'none', boxSizing:'border-box',
};

function Label({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                  letterSpacing:1, color:C.muted, marginBottom:4 }}>
      {children}{required && <span style={{ color:C.red }}> *</span>}
    </div>
  );
}

function Btn({ children, onClick, variant='primary', disabled, style }:
  { children:React.ReactNode; onClick?:()=>void; variant?:string; disabled?:boolean; style?: React.CSSProperties }) {
  const bg = variant==='danger' ? C.red : variant==='ghost' ? 'transparent' : C.gold;
  const co = variant==='ghost' ? C.muted : '#000';
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ background:bg, color:co, border: variant==='ghost'?`1px solid ${C.border}`:'none',
               borderRadius:6, padding:'7px 14px', fontSize:12, fontWeight:700,
               cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1, ...style }}>
      {children}
    </button>
  );
}

interface Variant { id:string; variantType:string; variantValue:string; stockQty:number; priceAdj:number; sku:string; isActive:boolean; }
interface Product {
  id:string; name:string; slug:string; description:string; shortDesc:string;
  category:string; price:number; comparePrice:number|null; currency:string;
  imageUrls:string[]; badge:string; sku:string; stockQty:number; lowStockAlert:number;
  isActive:boolean; isFeatured:boolean; isDigital:boolean; allowsVariants:boolean;
  weight:number|null; sortOrder:number; salesCount:number; festivalYear:number;
  variants:Variant[];
}
interface Stats { total:number; active:number; totalStock:number; lowStockCount:number; totalValue:number; lowStockItems:{id:string;name:string;stockQty:number}[]; }

const emptyProduct = () => ({
  name:'', description:'', shortDesc:'', category:'Apparel',
  price:'', comparePrice:'', currency:'NGN', imageUrls:[] as string[],
  badge:'', sku:'', stockQty:'0', lowStockAlert:'5',
  isActive:true, isFeatured:false, isDigital:false, allowsVariants:false,
  weight:'', sortOrder:'0',
});

const emptyVariant = () => ({ variantType:'size', variantValue:'', stockQty:'0', priceAdj:'0', sku:'' });

interface Props { currentUser?: { email?:string; systemRole?:string; role?:string } | null; }

export default function StoreAdminPanel({ currentUser }: Props) {
  const [products, setProducts]       = useState<Product[]>([]);
  const [stats, setStats]             = useState<Stats|null>(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [tab, setTab]                 = useState<'products'|'inventory'>('products');
  const [toast, setToast]             = useState<{msg:string;type:'ok'|'err'}|null>(null);
  const [expandedId, setExpandedId]   = useState<string|null>(null);
  const [showForm, setShowForm]       = useState(false);
  const [editProduct, setEditProduct] = useState<Product|null>(null);
  const [form, setForm]               = useState(emptyProduct());
  const [variantForm, setVariantForm] = useState(emptyVariant());
  const [addingVariantTo, setAddingVariantTo] = useState<string|null>(null);
  const [uploadingFor, setUploadingFor]       = useState<string|null>(null);
  const [deleteConfirm, setDeleteConfirm]     = useState<Product|null>(null);
  const [inventoryReport, setInventoryReport] = useState<any[]>([]);

  const showToast = (msg:string, type:'ok'|'err'='ok') => {
    setToast({msg,type});
    setTimeout(()=>setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/store/admin', { headers: authHdr() });
      const d = await r.json();
      if (d.success) { setProducts(d.products||[]); setStats(d.stats||null); }
    } catch { showToast('Failed to load store','err'); }
    setLoading(false);
  }, []);

  const loadInventory = useCallback(async () => {
    try {
      const r = await fetch('/api/store/admin/inventory', { headers: authHdr() });
      const d = await r.json();
      if (d.success) setInventoryReport(d.report||[]);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab==='inventory') loadInventory(); }, [tab, loadInventory]);

  const upd = (k: string, v: any) => setForm(f => ({...f, [k]:v}));

  const openAdd = () => {
    setEditProduct(null);
    setForm(emptyProduct());
    setShowForm(true);
  };

  const openEdit = (p: Product) => {
    setEditProduct(p);
    setForm({
      name:p.name, description:p.description||'', shortDesc:p.shortDesc||'',
      category:p.category, price:String(p.price), comparePrice:p.comparePrice?String(p.comparePrice):'',
      currency:p.currency, imageUrls:p.imageUrls||[], badge:p.badge||'', sku:p.sku||'',
      stockQty:String(p.stockQty), lowStockAlert:String(p.lowStockAlert),
      isActive:p.isActive, isFeatured:p.isFeatured, isDigital:p.isDigital,
      allowsVariants:p.allowsVariants, weight:p.weight?String(p.weight):'', sortOrder:String(p.sortOrder),
    } as any);
    setShowForm(true);
  };

  const saveProduct = async () => {
    if (!form.name || !form.category || !form.price)
      return showToast('Name, category and price are required','err');
    setSaving(true);
    try {
      const payload = {
        ...form, price:parseFloat(String(form.price)),
        comparePrice: form.comparePrice ? parseFloat(String(form.comparePrice)) : null,
        stockQty:parseInt(String(form.stockQty)||'0'),
        lowStockAlert:parseInt(String(form.lowStockAlert)||'5'),
        weight: form.weight ? parseFloat(String(form.weight)) : null,
        sortOrder:parseInt(String(form.sortOrder)||'0'),
      };
      const url  = editProduct ? `/api/store/admin/products/${editProduct.id}` : '/api/store/admin/products';
      const meth = editProduct ? 'PUT' : 'POST';
      const r    = await fetch(url, { method:meth, headers:authHdr(), body:JSON.stringify(payload) });
      const d    = await r.json();
      if (d.success) {
        showToast(editProduct ? 'Product updated' : 'Product created');
        setShowForm(false); setEditProduct(null);
        load();
      } else { showToast(d.error||'Save failed','err'); }
    } catch (e:any) { showToast(e.message,'err'); }
    setSaving(false);
  };

  const deleteProduct = async (p: Product) => {
    try {
      const r = await fetch(`/api/store/admin/products/${p.id}`, { method:'DELETE', headers:authHdr() });
      const d = await r.json();
      if (d.success) { showToast('Product deleted'); setDeleteConfirm(null); load(); }
      else showToast(d.error||'Delete failed','err');
    } catch (e:any) { showToast(e.message,'err'); }
  };

  const toggleProduct = async (p: Product) => {
    try {
      const r = await fetch(`/api/store/admin/products/${p.id}/toggle`, { method:'POST', headers:authHdr() });
      const d = await r.json();
      if (d.success) { showToast(d.isActive?'Product activated':'Product deactivated'); load(); }
    } catch {}
  };

  const addVariant = async (productId: string) => {
    if (!variantForm.variantValue) return showToast('Variant value required','err');
    try {
      const r = await fetch(`/api/store/admin/products/${productId}/variants`, {
        method:'POST', headers:authHdr(),
        body:JSON.stringify({ ...variantForm, stockQty:parseInt(String(variantForm.stockQty)||'0'),
          priceAdj:parseFloat(String(variantForm.priceAdj)||'0') })
      });
      const d = await r.json();
      if (d.success) { showToast('Variant added'); setAddingVariantTo(null); setVariantForm(emptyVariant()); load(); }
      else showToast(d.error||'Failed','err');
    } catch (e:any) { showToast(e.message,'err'); }
  };

  const deleteVariant = async (variantId: string) => {
    try {
      const r = await fetch(`/api/store/admin/variants/${variantId}`, { method:'DELETE', headers:authHdr() });
      const d = await r.json();
      if (d.success) { showToast('Variant removed'); load(); }
    } catch {}
  };

  const uploadImage = async (file: File, productId?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    setUploadingFor(productId||'new');
    try {
      const r = await fetch('/api/store/admin/upload-image', {
        method:'POST',
        headers:{ Authorization:`Bearer ${localStorage.getItem('erue_auth_token')||''}` },
        body:fd
      });
      const d = await r.json();
      if (d.success) {
        if (productId) {
          const p = products.find(x=>x.id===productId);
          if (p) {
            const newUrls = [...(p.imageUrls||[]), d.url];
            await fetch(`/api/store/admin/products/${productId}`, {
              method:'PUT', headers:authHdr(), body:JSON.stringify({ imageUrls:newUrls })
            });
            load();
          }
        } else {
          upd('imageUrls', [...((form.imageUrls as string[])||[]), d.url]);
        }
        showToast('Image uploaded');
      } else showToast(d.error||'Upload failed','err');
    } catch (e:any) { showToast(e.message,'err'); }
    setUploadingFor(null);
  };

  const removeImage = (idx: number) => {
    const urls = [...((form.imageUrls as string[])||[])];
    urls.splice(idx,1);
    upd('imageUrls', urls);
  };

  const formatNGN = (n:number) => `₦${n.toLocaleString()}`;

  const variantValueOptions = (type:string) => {
    if (type==='size')   return SIZE_VALUES;
    if (type==='colour') return COLOUR_VALUES;
    if (type==='gender') return GENDER_VALUES;
    return [];
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ background:C.bg, minHeight:'100vh', padding:'20px 16px', fontFamily:'Plus Jakarta Sans, sans-serif' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999,
                      background:toast.type==='ok'?C.green:C.red, color:'#fff',
                      borderRadius:8, padding:'10px 18px', fontSize:13, fontWeight:700, boxShadow:'0 4px 20px rgba(0,0,0,0.4)' }}>
          {toast.type==='ok'?'✅':'❌'} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ fontFamily:'Cinzel, serif', color:C.gold, margin:0, fontSize:20 }}>Festival Store Management</h2>
          <p style={{ color:C.muted, fontSize:12, margin:'4px 0 0' }}>Add products, manage stock, upload images, set variants</p>
        </div>
        <Btn onClick={openAdd}><Plus size={14} style={{ marginRight:4 }}/>Add Product</Btn>
      </div>

      {/* Stats bar */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10, marginBottom:20 }}>
          {[
            { label:'Total Products', value:stats.total,      color:C.gold },
            { label:'Active',         value:stats.active,     color:C.green },
            { label:'Total Stock',    value:stats.totalStock, color:C.goldLt },
            { label:'Low Stock ⚠️',  value:stats.lowStockCount, color:stats.lowStockCount>0?C.red:C.muted },
            { label:'Inventory Value',value:formatNGN(stats.totalValue), color:C.goldLt },
          ].map(s=>(
            <div key={s.label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontSize:18, fontWeight:800, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Low stock alert */}
      {stats && stats.lowStockCount > 0 && (
        <div style={{ background:'rgba(239,68,68,0.08)', border:`1px solid ${C.red}33`, borderRadius:8,
                      padding:'10px 14px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
          <AlertTriangle size={16} color={C.red}/>
          <span style={{ color:C.red, fontSize:12, fontWeight:700 }}>
            Low Stock: {stats.lowStockItems.map(i=>`${i.name} (${i.stockQty})`).join(' · ')}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {(['products','inventory'] as const).map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{ padding:'6px 16px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer',
                     background:tab===t?C.gold:'transparent', color:tab===t?'#000':C.muted,
                     border:`1px solid ${tab===t?C.gold:C.border}` }}>
            {t==='products'?'🛍️ Products':'📦 Inventory'}
          </button>
        ))}
      </div>

      {/* PRODUCTS TAB */}
      {tab==='products' && (
        <div>
          {loading && <div style={{ color:C.muted, textAlign:'center', padding:40 }}>Loading...</div>}
          {!loading && products.length===0 && (
            <div style={{ background:C.card, border:`1px dashed ${C.border}`, borderRadius:12,
                          padding:48, textAlign:'center' }}>
              <Package size={40} color={C.muted} style={{ margin:'0 auto 12px' }}/>
              <div style={{ color:C.muted, fontSize:14 }}>No products yet. Click "Add Product" to get started.</div>
            </div>
          )}
          {!loading && products.map(p=>(
            <div key={p.id} style={{ background:C.card, border:`1px solid ${p.stockQty<=p.lowStockAlert&&!p.isDigital?C.red+'44':C.border}`,
                                      borderRadius:10, marginBottom:12, overflow:'hidden' }}>
              {/* Product row */}
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px' }}>
                {/* Thumbnail */}
                <div style={{ width:52, height:52, borderRadius:8, background:C.border, overflow:'hidden', flexShrink:0,
                               display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {p.imageUrls&&p.imageUrls[0]
                    ? <img src={p.imageUrls[0]} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                    : <Image size={20} color={C.muted}/>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ color:C.text, fontWeight:700, fontSize:14 }}>{p.name}</span>
                    {p.badge && <span style={{ background:C.gold+'22', color:C.gold, fontSize:10, fontWeight:700,
                                               padding:'2px 8px', borderRadius:20, border:`1px solid ${C.gold}44` }}>{p.badge}</span>}
                    {p.isFeatured && <span style={{ background:'#7c3aed22', color:'#a78bfa', fontSize:10,
                                                    fontWeight:700, padding:'2px 8px', borderRadius:20 }}>⭐ Featured</span>}
                    {p.isDigital && <span style={{ background:'#0ea5e922', color:'#38bdf8', fontSize:10,
                                                    fontWeight:700, padding:'2px 8px', borderRadius:20 }}>Digital</span>}
                  </div>
                  <div style={{ color:C.muted, fontSize:11, marginTop:2 }}>
                    {p.category} · {formatNGN(p.price)}
                    {p.comparePrice && <span style={{ textDecoration:'line-through', marginLeft:6, color:C.red }}>{formatNGN(p.comparePrice)}</span>}
                    {!p.isDigital && <span style={{ marginLeft:8, color:p.stockQty<=p.lowStockAlert?C.red:C.green }}>
                      Stock: {p.stockQty}{p.stockQty<=p.lowStockAlert?' ⚠️':''}
                    </span>}
                    {p.variants.length>0 && <span style={{ marginLeft:8, color:C.muted }}>{p.variants.length} variant{p.variants.length>1?'s':''}</span>}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                  <button onClick={()=>toggleProduct(p)} title={p.isActive?'Deactivate':'Activate'}
                    style={{ background:'none', border:'none', cursor:'pointer', padding:4 }}>
                    {p.isActive ? <ToggleRight size={22} color={C.green}/> : <ToggleLeft size={22} color={C.muted}/>}
                  </button>
                  <Btn variant='ghost' onClick={()=>openEdit(p)} style={{ padding:'5px 10px' }}>
                    <Edit2 size={13}/>
                  </Btn>
                  <Btn variant='danger' onClick={()=>setDeleteConfirm(p)} style={{ padding:'5px 10px' }}>
                    <Trash2 size={13}/>
                  </Btn>
                  <button onClick={()=>setExpandedId(expandedId===p.id?null:p.id)}
                    style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:4 }}>
                    {expandedId===p.id?<ChevronUp size={16}/>:<ChevronDown size={16}/>}
                  </button>
                </div>
              </div>

              {/* Expanded panel */}
              {expandedId===p.id && (
                <div style={{ borderTop:`1px solid ${C.border}`, padding:'14px 16px', background:'#0A120D' }}>
                  {/* Image gallery */}
                  <div style={{ marginBottom:14 }}>
                    <Label>Product Images</Label>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                      {(p.imageUrls||[]).map((url,i)=>(
                        <div key={i} style={{ width:64, height:64, borderRadius:6, overflow:'hidden', position:'relative', border:`1px solid ${C.border}` }}>
                          <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                        </div>
                      ))}
                      <label style={{ width:64, height:64, borderRadius:6, border:`2px dashed ${C.border}`,
                                       display:'flex', alignItems:'center', justifyContent:'center',
                                       cursor:'pointer', color:C.muted }}>
                        {uploadingFor===p.id ? '...' : <Upload size={18}/>}
                        <input type="file" accept="image/*" style={{ display:'none' }}
                          onChange={e=>{ if(e.target.files?.[0]) uploadImage(e.target.files[0], p.id); }}/>
                      </label>
                    </div>
                  </div>

                  {/* Variants */}
                  <div>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                      <Label>Variants (size / colour / type / material / gender)</Label>
                      <Btn variant='ghost' onClick={()=>setAddingVariantTo(addingVariantTo===p.id?null:p.id)}
                        style={{ padding:'4px 10px', fontSize:11 }}>
                        <Plus size={12} style={{ marginRight:3 }}/>Add Variant
                      </Btn>
                    </div>

                    {p.variants.length===0 && addingVariantTo!==p.id &&
                      <div style={{ color:C.muted, fontSize:12 }}>No variants — product sells as single item.</div>}

                    {p.variants.map(v=>(
                      <div key={v.id} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6,
                                               background:C.card, borderRadius:6, padding:'6px 10px', border:`1px solid ${C.border}` }}>
                        <Tag size={12} color={C.gold}/>
                        <span style={{ color:C.goldLt, fontSize:12, fontWeight:700, textTransform:'capitalize' }}>{v.variantType}:</span>
                        <span style={{ color:C.text, fontSize:12 }}>{v.variantValue}</span>
                        <span style={{ color:C.muted, fontSize:11 }}>Stock: {v.stockQty}</span>
                        {v.priceAdj!==0 && <span style={{ color:v.priceAdj>0?C.green:C.red, fontSize:11 }}>
                          {v.priceAdj>0?'+':''}{formatNGN(v.priceAdj)}
                        </span>}
                        <div style={{ flex:1 }}/>
                        <button onClick={()=>deleteVariant(v.id)}
                          style={{ background:'none', border:'none', cursor:'pointer', color:C.red, padding:2 }}>
                          <X size={13}/>
                        </button>
                      </div>
                    ))}

                    {/* Add variant form */}
                    {addingVariantTo===p.id && (
                      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:8, padding:12, marginTop:8 }}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 80px 80px', gap:8, marginBottom:8 }}>
                          <div>
                            <Label>Type</Label>
                            <select value={variantForm.variantType} onChange={e=>setVariantForm(f=>({...f,variantType:e.target.value,variantValue:''}))} style={iS}>
                              {VARIANT_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label required>Value</Label>
                            {variantValueOptions(variantForm.variantType).length>0
                              ? <select value={variantForm.variantValue} onChange={e=>setVariantForm(f=>({...f,variantValue:e.target.value}))} style={iS}>
                                  <option value="">Select...</option>
                                  {variantValueOptions(variantForm.variantType).map(v=><option key={v} value={v}>{v}</option>)}
                                  <option value="Custom">Custom...</option>
                                </select>
                              : <input value={variantForm.variantValue} onChange={e=>setVariantForm(f=>({...f,variantValue:e.target.value}))}
                                  placeholder="e.g. Cotton" style={iS}/>}
                          </div>
                          <div>
                            <Label>Stock</Label>
                            <input type="number" value={variantForm.stockQty} onChange={e=>setVariantForm(f=>({...f,stockQty:e.target.value as any}))} style={iS}/>
                          </div>
                          <div>
                            <Label>±Price</Label>
                            <input type="number" value={variantForm.priceAdj} onChange={e=>setVariantForm(f=>({...f,priceAdj:e.target.value as any}))} style={iS}/>
                          </div>
                        </div>
                        {variantForm.variantValue==='Custom' && (
                          <div style={{ marginBottom:8 }}>
                            <Label required>Custom Value</Label>
                            <input value="" onChange={e=>setVariantForm(f=>({...f,variantValue:e.target.value}))} placeholder="Enter custom value" style={iS}/>
                          </div>
                        )}
                        <div style={{ display:'flex', gap:8 }}>
                          <Btn onClick={()=>addVariant(p.id)}>Save Variant</Btn>
                          <Btn variant='ghost' onClick={()=>setAddingVariantTo(null)}>Cancel</Btn>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* INVENTORY TAB */}
      {tab==='inventory' && (
        <div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
            <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}`,
                           display:'flex', alignItems:'center', gap:8 }}>
              <BarChart2 size={16} color={C.gold}/>
              <span style={{ color:C.gold, fontWeight:700, fontSize:14 }}>Stock & Inventory Report</span>
            </div>
            {inventoryReport.length===0
              ? <div style={{ padding:40, textAlign:'center', color:C.muted, fontSize:13 }}>No products to report</div>
              : <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                    <thead>
                      <tr style={{ background:'#0A120D' }}>
                        {['Product','Category','SKU','Stock','Alert','Status','Price','Value','Sales'].map(h=>(
                          <th key={h} style={{ padding:'10px 12px', textAlign:'left', color:C.muted,
                                               fontSize:10, textTransform:'uppercase', letterSpacing:1,
                                               borderBottom:`1px solid ${C.border}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryReport.map((p,i)=>(
                        <tr key={p.id} style={{ background:i%2===0?'transparent':'#0A120D',
                                                 borderBottom:`1px solid ${C.border}22` }}>
                          <td style={{ padding:'9px 12px', color:C.text, fontWeight:600 }}>{p.name}</td>
                          <td style={{ padding:'9px 12px', color:C.muted }}>{p.category}</td>
                          <td style={{ padding:'9px 12px', color:C.muted }}>{p.sku||'—'}</td>
                          <td style={{ padding:'9px 12px', color:p.isLow?C.red:C.green, fontWeight:700 }}>
                            {p.isDigital?'∞':p.stockQty}
                          </td>
                          <td style={{ padding:'9px 12px', color:C.muted }}>{p.isDigital?'—':p.lowStockAlert}</td>
                          <td style={{ padding:'9px 12px' }}>
                            {p.isDigital
                              ? <span style={{ color:'#38bdf8', fontSize:10, fontWeight:700 }}>DIGITAL</span>
                              : p.isLow
                                ? <span style={{ color:C.red, fontSize:10, fontWeight:700 }}>⚠️ LOW</span>
                                : <span style={{ color:C.green, fontSize:10, fontWeight:700 }}>OK</span>}
                          </td>
                          <td style={{ padding:'9px 12px', color:C.goldLt }}>₦{p.price.toLocaleString()}</td>
                          <td style={{ padding:'9px 12px', color:C.gold, fontWeight:700 }}>₦{p.totalValue.toLocaleString()}</td>
                          <td style={{ padding:'9px 12px', color:C.muted }}>{p.salesCount}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:'#0A120D', borderTop:`1px solid ${C.border}` }}>
                        <td colSpan={7} style={{ padding:'10px 12px', color:C.muted, fontWeight:700, fontSize:11 }}>TOTAL INVENTORY VALUE</td>
                        <td style={{ padding:'10px 12px', color:C.gold, fontWeight:800, fontSize:14 }}>
                          ₦{inventoryReport.reduce((s,p)=>s+p.totalValue,0).toLocaleString()}
                        </td>
                        <td style={{ padding:'10px 12px', color:C.muted }}>
                          {inventoryReport.reduce((s,p)=>s+p.salesCount,0)} sold
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>}
          </div>
        </div>
      )}

      {/* ADD/EDIT PRODUCT MODAL */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000,
                       display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12,
                         width:'100%', maxWidth:640, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                           padding:'16px 20px', borderBottom:`1px solid ${C.border}` }}>
              <h3 style={{ fontFamily:'Cinzel, serif', color:C.gold, margin:0, fontSize:16 }}>
                {editProduct?'Edit Product':'Add New Product'}
              </h3>
              <button onClick={()=>setShowForm(false)} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer' }}>
                <X size={18}/>
              </button>
            </div>
            <div style={{ padding:'20px' }}>

              {/* Basic info */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <Label required>Product Name</Label>
                  <input value={form.name} onChange={e=>upd('name',e.target.value)} placeholder="e.g. ERUE 2026 Official T-Shirt" style={iS}/>
                </div>
                <div>
                  <Label required>Category</Label>
                  <select value={form.category} onChange={e=>upd('category',e.target.value)} style={iS}>
                    {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Badge</Label>
                  <input value={form.badge} onChange={e=>upd('badge',e.target.value)} placeholder="e.g. Bestseller, Limited Edition" style={iS}/>
                </div>
                <div>
                  <Label required>Price (₦)</Label>
                  <input type="number" value={form.price} onChange={e=>upd('price',e.target.value)} placeholder="8000" style={iS}/>
                </div>
                <div>
                  <Label>Compare Price (₦) — for sale display</Label>
                  <input type="number" value={form.comparePrice} onChange={e=>upd('comparePrice',e.target.value)} placeholder="12000" style={iS}/>
                </div>
                <div>
                  <Label>SKU</Label>
                  <input value={form.sku} onChange={e=>upd('sku',e.target.value)} placeholder="ERUE-TS-001" style={iS}/>
                </div>
                <div>
                  <Label>Sort Order</Label>
                  <input type="number" value={form.sortOrder} onChange={e=>upd('sortOrder',e.target.value)} style={iS}/>
                </div>
              </div>

              <div style={{ marginBottom:12 }}>
                <Label>Short Description</Label>
                <input value={form.shortDesc} onChange={e=>upd('shortDesc',e.target.value)}
                  placeholder="Brief one-line description" style={iS}/>
              </div>

              <div style={{ marginBottom:12 }}>
                <Label>Full Description</Label>
                <textarea value={form.description} onChange={e=>upd('description',e.target.value)}
                  placeholder="Detailed product description..." rows={3}
                  style={{ ...iS, resize:'vertical' }}/>
              </div>

              {/* Images */}
              <div style={{ marginBottom:12 }}>
                <Label>Product Images</Label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                  {((form.imageUrls as string[])||[]).map((url,i)=>(
                    <div key={i} style={{ position:'relative', width:64, height:64, borderRadius:6, overflow:'hidden', border:`1px solid ${C.border}` }}>
                      <img src={url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      <button onClick={()=>removeImage(i)}
                        style={{ position:'absolute', top:2, right:2, background:'rgba(0,0,0,0.7)',
                                  border:'none', borderRadius:3, cursor:'pointer', color:'#fff', padding:'1px 3px', lineHeight:1 }}>
                        <X size={10}/>
                      </button>
                    </div>
                  ))}
                  <label style={{ width:64, height:64, borderRadius:6, border:`2px dashed ${C.border}`,
                                   display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                                   cursor:'pointer', color:C.muted, fontSize:10, gap:3 }}>
                    {uploadingFor==='new' ? '...' : <><Upload size={16}/><span>Upload</span></>}
                    <input type="file" accept="image/*" style={{ display:'none' }}
                      onChange={e=>{ if(e.target.files?.[0]) uploadImage(e.target.files[0]); }}/>
                  </label>
                </div>
                <div style={{ marginTop:6 }}>
                  <Label>Or paste image URL</Label>
                  <input placeholder="https://..." style={iS}
                    onBlur={e=>{ if(e.target.value.trim()) { upd('imageUrls',[...((form.imageUrls as string[])||[]),e.target.value.trim()]); e.target.value=''; }}}/>
                </div>
              </div>

              {/* Stock */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
                <div>
                  <Label>Stock Quantity</Label>
                  <input type="number" value={form.stockQty} onChange={e=>upd('stockQty',e.target.value)} style={iS} disabled={!!form.isDigital}/>
                </div>
                <div>
                  <Label>Low Stock Alert At</Label>
                  <input type="number" value={form.lowStockAlert} onChange={e=>upd('lowStockAlert',e.target.value)} style={iS} disabled={!!form.isDigital}/>
                </div>
                <div>
                  <Label>Weight (kg)</Label>
                  <input type="number" value={form.weight} onChange={e=>upd('weight',e.target.value)} placeholder="0.5" style={iS}/>
                </div>
              </div>

              {/* Toggles */}
              <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginBottom:20 }}>
                {([
                  ['isActive','Active (visible in store)'],
                  ['isFeatured','Featured (show on homepage)'],
                  ['isDigital','Digital product (no stock tracking)'],
                  ['allowsVariants','Has variants (size/colour/etc)'],
                ] as [string,string][]).map(([k,label])=>(
                  <label key={k} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:C.text }}>
                    <input type="checkbox" checked={!!(form as any)[k]} onChange={e=>upd(k,e.target.checked)}
                      style={{ accentColor:C.gold, width:14, height:14 }}/>
                    {label}
                  </label>
                ))}
              </div>

              <div style={{ display:'flex', gap:10 }}>
                <Btn onClick={saveProduct} disabled={saving}>{saving?'Saving...':'Save Product'}</Btn>
                <Btn variant='ghost' onClick={()=>setShowForm(false)}>Cancel</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.8)', zIndex:1000,
                       display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:C.card, border:`1px solid ${C.red}44`, borderRadius:12, padding:28, maxWidth:380, width:'100%' }}>
            <h3 style={{ color:C.red, fontFamily:'Cinzel, serif', marginBottom:8 }}>Delete Product?</h3>
            <p style={{ color:C.muted, fontSize:13, marginBottom:20 }}>
              "{deleteConfirm.name}" and all its variants will be permanently deleted. This cannot be undone.
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <Btn variant='danger' onClick={()=>deleteProduct(deleteConfirm)}>Delete</Btn>
              <Btn variant='ghost' onClick={()=>setDeleteConfirm(null)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
